import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Modal, ProgressBar } from "react-bootstrap";
import api from "common/api";
import { toast } from "react-toastify";
import { FaTrash } from "react-icons/fa";

type Props = {
  userId: string;
  initialImageUrl: string | null;
};

const TeacherPictureUpload: React.FC<Props> = ({ userId, initialImageUrl }) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialImageUrl) {
      setPreviewUrl(initialImageUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [initialImageUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setPreviewUrl(URL.createObjectURL(file));
      uploadImage(file);
    }
  };

  const uploadImage = async (file: File) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append("picture", file);

    try {
      const response = await api.post(
        `/teachers/${userId}/picture/`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setUploadProgress(percentCompleted);
            }
          },
        },
      );

      const uploadedImageUrl = response.data.picture;
      setPreviewUrl(uploadedImageUrl);

      toast.success("Slika je uspješno učitana.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading image: ", error);
      toast.error("Greška prilikom učitavanja slike.");
      setPreviewUrl(null);
    }

    setIsUploading(false);
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDeletePicture = async () => {
    try {
      await api.delete(`/teachers/${userId}/picture/`);
      setPreviewUrl(null);
      toast.success("Slika je uspješno obrisana.");
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting picture:", error);
      toast.error("Greška prilikom brisanja slike.");
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <Modal show={showDeleteModal} onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Potvrda brisanja</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Jeste li sigurni da želite obrisati sliku nastavnika?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={closeDeleteModal}>
            Odustani
          </Button>
          <Button variant="primary" onClick={handleDeletePicture}>
            Obriši
          </Button>
        </Modal.Footer>
      </Modal>

      <Form.Group as={Col} controlId="formPicture" className="mb-3">
        <Form.Label>Slika</Form.Label>
        <Form.Control
          type="file"
          name="picture"
          onChange={handleFileChange}
          disabled={isUploading}
          className="mb-2"
          ref={fileInputRef}
        />

        {isUploading && (
          <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} />
        )}
      </Form.Group>

      {previewUrl && (
        <div className="mb-3">
          <img
            src={previewUrl}
            alt="Preview"
            style={{ width: "250px", height: "auto", borderRadius: "10px" }}
            className="mb-2"
          />
          <div>
            <Button
              variant="primary"
              onClick={openDeleteModal}
              className="mt-2"
            >
              <FaTrash className="me-2" /> Obriši sliku
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default TeacherPictureUpload;
